import { Component, computed, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BuildingEquipmentFilterDto,
  BuildingEquipmentItemDto,
  BuildingEquipmentService,
  BuildingService,
  EquipmentItemDto,
  EquipmentService,
  TenantDetailDto,
  TenantService
} from '@artemis-software/wr-api';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BuildingDetailDto } from '@artemis-software/wr-api/model/buildingDetailDto';
import { DataTableComponent } from '@shared/generic/data-table/data-table.component';
import { CurrentUserService } from '@services/current-user.service';

@Component({
  standalone: false,
  selector: 'wr-internal-tga-type-overview-page',
  templateUrl: './internal-tga-overview-page.component.html',
  styleUrls: ['./internal-tga-overview-page.component.scss'],
})
export class InternalTgaOverviewPageComponent {
  @ViewChild(DataTableComponent) dataTable!: DataTableComponent<BuildingEquipmentItemDto, BuildingEquipmentFilterDto>;
  canCreate = computed(() => this.currentUserService.hasPrivilege('canCreateTgas'));
  form!: FormGroup;

  readonly loadBuildingEquipments = (filter: BuildingEquipmentFilterDto) => this.buildingEquipmentService.findAll(filter);
  readonly getCount = (filter: BuildingEquipmentFilterDto) => this.buildingEquipmentService.getCount(filter);

  constructor(private readonly router: Router,
              private readonly activeRoute: ActivatedRoute,
              private readonly formBuilder: FormBuilder,
              private readonly buildingEquipmentService: BuildingEquipmentService,
              private readonly buildingService: BuildingService,
              private readonly currentUserService: CurrentUserService,
              private readonly equipmentService: EquipmentService,
              private readonly tenantService: TenantService) {
    this.form = this.formBuilder.group({
      text: this.formBuilder.control(''),
      ownerTenantId: this.formBuilder.control<string | null>(null),
      ownerTenant: this.formBuilder.control<TenantDetailDto | null>(null),
      creatorTenantId: this.formBuilder.control<string | null>(null),
      equipmentId: this.formBuilder.control<string | null>(null),
      equipment: this.formBuilder.control(undefined),
      buildingId: this.formBuilder.control<string | null>(null),
      building: this.formBuilder.control(undefined),
      facilityElement: this.formBuilder.control<string | null>(null),
      customFieldFilters: this.formBuilder.control(undefined),
    });

    this.form.get('ownerTenantId')?.valueChanges.subscribe(() => {
      this.form.get('building')?.setValue(undefined);
    });

    this.form.get('creatorTenantId')?.valueChanges.subscribe(() => {
      this.form.get('equipment')?.setValue(undefined);
    });

    this.form.get('equipment')?.valueChanges.subscribe((equipment: EquipmentItemDto) => {
      this.form.get('equipmentId')?.setValue(equipment?.id);
      this.form.get('customFieldFilters')?.setValue(undefined);
    });

    this.form.get('building')?.valueChanges.subscribe((building: BuildingDetailDto) => {
      this.form.get('buildingId')?.setValue(building?.id);
    });

    this.form.get('ownerTenant')?.valueChanges.subscribe((tenant: TenantDetailDto) => {
      this.form.get('ownerTenantId')?.setValue(tenant?.id);
    });

    this.activeRoute.queryParams.subscribe((params) => {
      if (params['buildingId']) {
        this.buildingService.findById(params['buildingId']).subscribe((building) => {
          this.form.get('building')?.setValue(building);
          this.dataTable.applyFormValueToFilter();
        });
      }
      if (params['equipmentId']) {
        this.equipmentService.findById(params['equipmentId']).subscribe((equipment) => {
          this.form.get('equipment')?.setValue(equipment);
          this.dataTable.applyFormValueToFilter();
        });
      }
      if (params['externalTenantId']) {
        this.tenantService.findById(params['externalTenantId']).subscribe((ownerTenant) => {
          this.form.get('ownerTenant')?.setValue(ownerTenant);
          this.dataTable.applyFormValueToFilter();
        });
      }
    });
  }

  navigateToBuildingEquipment(buildingEquipment: BuildingEquipmentItemDto): void {
    this.router.navigate(['buildingEquipment', buildingEquipment.id]);
  }

  add(): void {
    this.router.navigate(['buildingEquipment', 'new']);
  }

  protected readonly TenantTypeEnum = TenantDetailDto.TypeEnum;
}
